import { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { StatusBadge } from "../components/StatusBadge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", email: "", password: "", category_ids: [], active: true };

export default function ProfessionalsPage() {
  const [profs, setProfs] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      api.get("/professionals"),
      api.get("/categories"),
    ]);
    setProfs(p);
    setCats(c);
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...empty });
    setEditingId(null);
  };
  const openEdit = (p) => {
    setForm({ name: p.name, email: p.email, password: "", category_ids: p.category_ids || [], active: p.active });
    setEditingId(p.id);
  };

  const toggleCat = (id) => {
    setForm((f) => {
      const has = f.category_ids.includes(id);
      return { ...f, category_ids: has ? f.category_ids.filter((x) => x !== id) : [...f.category_ids, id] };
    });
  };

  const submit = async () => {
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/professionals/${editingId}`, payload);
        toast.success("Profissional atualizada");
      } else {
        await api.post("/professionals", form);
        toast.success("Profissional cadastrada");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir ${p.name}?`)) return;
    try {
      await api.delete(`/professionals/${p.id}`);
      toast.success("Excluída");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const catMap = Object.fromEntries(cats.map((c) => [c.id, c.name]));

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Equipe</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Profissionais</h1>
        </div>
        <button
          data-testid="btn-new-prof"
          onClick={openNew}
          className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:-translate-y-[1px] transition-transform"
        >
          <Plus size={16} /> Nova profissional
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="hidden md:table-cell px-4 py-3">E-mail</th>
              <th className="hidden lg:table-cell px-4 py-3">Categorias</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Ativa</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {profs.length === 0 && (
              <tr>
                <td colSpan={6} className="py-10 text-center text-gray-400">
                  Nenhuma profissional cadastrada
                </td>
              </tr>
            )}
            {profs.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`prof-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="hidden md:table-cell px-4 py-3 text-gray-600">{p.email}</td>
                <td className="hidden lg:table-cell px-4 py-3 text-gray-600">
                  {(p.category_ids || []).map((id) => catMap[id]).filter(Boolean).join(", ") || "—"}
                </td>
                <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                <td className="px-4 py-3">
                  <span className={p.active ? "text-green-600" : "text-gray-400"}>
                    {p.active ? "Sim" : "Não"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      data-testid={`btn-edit-prof-${p.id}`}
                      onClick={() => openEdit(p)}
                      className="rounded-full border border-gray-200 p-2 hover:bg-gray-50"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      data-testid={`btn-del-prof-${p.id}`}
                      onClick={() => remove(p)}
                      className="rounded-full border border-gray-200 p-2 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="dialog-prof">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? "Editar profissional" : "Nova profissional"}
            </DialogTitle>
            <DialogDescription>
              Cadastre nome, e-mail, senha inicial e as categorias que essa profissional atende.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Nome">
                <input
                  data-testid="input-prof-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                />
              </Field>
              <Field label="Login / E-mail">
                <input
                  data-testid="input-prof-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                  type="email"
                />
              </Field>
              <Field label={editingId ? "Nova senha (opcional)" : "Senha inicial"}>
                <input
                  data-testid="input-prof-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                  type="text"
                />
              </Field>
              <div>
                <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">
                  Categorias
                </label>
                <div className="flex flex-wrap gap-2">
                  {cats.map((c) => {
                    const active = form.category_ids.includes(c.id);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        data-testid={`chip-cat-${c.name.toLowerCase()}`}
                        onClick={() => toggleCat(c.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          active ? "border-black bg-black text-white" : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="input-prof-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Ativa
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setForm(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">Cancelar</button>
            <button
              data-testid="btn-save-prof"
              onClick={submit}
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white"
            >
              Salvar profissional
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">{label}</label>
      {children}
    </div>
  );
}
